import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import EditLanguageForm from "@/components/EditLanguageForm";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/languages/$id/")({
  component: EditLanguagePage,
});

function EditLanguagePage() {
  const { id } = Route.useParams();
  return <EditLanguagePageComponent id={id} />;
}

// Exported for testing
export function EditLanguagePageComponent({ id }: { id: string }) {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<FormOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    getFormOptions("languages")
      .then((options) => {
        if (!active) return;
        const found = options.find((option) => option.id === id);
        if (!found) {
          setNotFound(true);
        } else {
          setLanguage(found);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching language:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const goToList = () => navigate({ to: "/dashboard/settings/languages" });

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !language) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Language not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EntityFormPage
      title="Edit Language"
      description={`Update ${language.label}'s catalog listing.`}
      backHref="/dashboard/settings/languages"
      headerActions={
        !showDeleteConfirm ? (
          <Button type="button" variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        ) : undefined
      }
    >
      <EditLanguageForm
        language={language}
        showDeleteConfirm={showDeleteConfirm}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onDeleted={goToList}
        onSaved={goToList}
      />
    </EntityFormPage>
  );
}
